<script setup>
import { onMounted, ref } from 'vue';

import InputError from './InputError.vue';
import InputLabel from './InputLabel.vue';
import InputWarning from './InputWarning.vue';

defineOptions({
    inheritAttrs: false,
});

defineProps({
    wrapperClasses: {
        type: String,
        default: '',
    },
    block: {
        type: Boolean,
        default: false,
    },
    label: String,
    warning: String,
    error: String,
});

const model = defineModel({
    type: [String, Number],
});

const input = ref(null);

onMounted(() => {
    if (input.value.hasAttribute('autofocus')) {
        input.value.focus();
    }
});

defineExpose({ focus: () => input.value.focus() });
</script>

<template>
    <div :class="[block ? 'w-full' : 'max-w-sm flex-grow', wrapperClasses]">
        <div class="flex items-center justify-between">
            <InputLabel :for="$attrs['id']">
                {{ label }}
            </InputLabel>
            <div v-if="$slots.extra" class="text-sm">
                <slot name="extra" />
            </div>
        </div>
        <div class="mt-2 flex items-center gap-3">
            <div
                class="flex flex-1 items-center gap-2 rounded-xl bg-white px-4 py-2.5 outline outline-1 -outline-offset-1 outline-neutral-300 focus-within:outline focus-within:-outline-offset-1 focus-within:outline-brand-500"
                :class="{
                    'outline-error-500 focus-within:outline-error-500': !!error,
                    'outline-warning-500 focus-within:outline-warning-500':
                        !!warning && !error,
                }"
            >
                <div v-if="$slots.leftIcon" class="flex items-center">
                    <slot name="leftIcon" />
                </div>
                <input
                    ref="input"
                    v-model="model"
                    type="text"
                    v-bind="$attrs"
                    class="min-w-0 flex-1 bg-transparent text-base font-medium text-neutral-800 placeholder:text-neutral-400 focus:outline-none disabled:cursor-not-allowed disabled:bg-neutral-50 disabled:text-neutral-400"
                    :class="{
                        'text-error-500 placeholder:text-error-500': !!error,
                        'text-warning-500 placeholder:text-warning-500':
                            !!warning && !error,
                    }"
                />
                <div v-if="$slots.rightIcon" class="flex items-center">
                    <slot name="rightIcon" />
                </div>
            </div>
            <slot name="rightActions" />
        </div>
        <InputWarning v-show="warning && !error" :message="warning" />
        <InputError :message="error" />
    </div>
</template>
